import { Editor, Extensions, Node } from '@tiptap/core'
import StarterKit from '@tiptap/starter-kit'
import { TaskList } from '@tiptap/extension-list'
import { Placeholder } from '@tiptap/extensions'
import { Markdown } from 'tiptap-markdown'
import { find } from 'linkifyjs'

// Only web and e-mail addresses become a link. A network path or a phone number stays plain text:
// tel: and ftp: are in tiptap's own default allow list, so they have to be filtered out explicitly.
const ALLOWED_LINK_SCHEMES = ['http', 'https', 'mailto']

const DEFAULT_LINK_PROTOCOL = 'https'

const LINK_ATTRIBUTES = { target: '_blank', rel: 'noopener noreferrer nofollow' }

const hasAllowedScheme = (url: string): boolean => {
    const scheme = /^([a-z][a-z0-9+.-]*):/i.exec(url)?.[1]?.toLowerCase()
    return !scheme || ALLOWED_LINK_SCHEMES.includes(scheme)
}

export interface RichTextExtensionsOptions {
    /**
     * The task item to register. The edit form renders a real checkbox, the read-only view draws a span.
     */
    taskItem: Node
    /**
     * Turns typed and pasted URLs into clickable links. Off by default: a description travels into customer
     * templates through `|raw`, so a field only gets links where that is wanted.
     */
    allowLinks?: boolean
    /**
     * Grey text shown while the editor is empty, like the placeholder of an ordinary input. Read-only views
     * leave it out: there is nothing to fill in there.
     */
    placeholder?: string
}

/**
 * The extension list shared by the editor (FormikRichTextField) and the read-only view (RichText).
 *
 * This list is also what sanitizes the content: a ProseMirror document can only hold nodes and marks from the
 * schema, and everything entering it (typing, paste, setContent) is parsed through that schema first. Which is
 * exactly why both sides must use the same list: a mark the editor writes but the view does not know is thrown
 * away when reading, so the user would lose formatting that was saved just fine.
 */
export function richTextExtensions(options: RichTextExtensionsOptions): Extensions {
    return [
        // StarterKit already registers underline, so no separate extension for it.
        StarterKit.configure({
            link: options.allowLinks
                ? {
                      openOnClick: true,
                      autolink: true,
                      defaultProtocol: DEFAULT_LINK_PROTOCOL,
                      // `protocols` only adds to tiptap's default allow list and cannot narrow it, so the
                      // restriction has to happen here. The autolinker validates the raw typed text, which has
                      // no scheme yet ("info@hammock.nl"), so input without a scheme has to pass.
                      isAllowedUri: (url, ctx) => ctx.defaultValidate(url) && hasAllowedScheme(url),
                      HTMLAttributes: LINK_ATTRIBUTES,
                  }
                : false,
        }),
        // TaskList/TaskItem are not part of StarterKit. TaskItem keeps its checkbox state in a `checked`
        // attribute, which getHTML() writes as data-checked - so ticking a box is an ordinary change to the
        // field value and needs no storage of its own.
        TaskList,
        options.taskItem,
        // `html: true` is needed because part of the content is not markdown at all: the editor stores what it
        // produces, which is HTML. With `html: false` markdown-it escapes those tags, so the user reads
        // `<p>omschrijving</p>` instead of seeing a paragraph.
        Markdown.configure({ html: true, breaks: true }),
        // The extension only writes a data-placeholder attribute; the text is drawn by CSS in the field itself.
        ...(options.placeholder ? [Placeholder.configure({ placeholder: options.placeholder })] : []),
    ]
}

/**
 * Links the URLs that autolink left alone. That plugin only looks at the word before the whitespace that was just
 * typed, so a URL at the very end of the text - the last thing typed before pressing Opslaan - never becomes a link.
 * The editor runs this on blur, over the whole document, using linkify: the same detector tiptap itself uses, so a
 * link made here is the link autolink would have made.
 *
 * Does nothing when the field was set up without `allowLinks`: the schema then holds no link mark at all.
 */
export function linkifyPlainText(editor: Editor): void {
    const { doc, schema, tr } = editor.state
    const linkType = schema.marks.link

    if (!linkType) {
        return
    }

    doc.descendants((node, pos, parent) => {
        if (!node.isText || !node.text || linkType.isInSet(node.marks)) {
            return
        }

        // Code is quoted, not addressed: autolink leaves both the code mark and a code block alone, so this pass
        // has to as well.
        if (schema.marks.code?.isInSet(node.marks) || parent?.type.spec.code) {
            return
        }

        find(node.text, { defaultProtocol: DEFAULT_LINK_PROTOCOL })
            .filter(link => link.isLink && hasAllowedScheme(link.href))
            .forEach(link => tr.addMark(pos + link.start, pos + link.end, linkType.create({ href: link.href, ...LINK_ATTRIBUTES })))
    })

    if (tr.steps.length > 0) {
        // Without the flag the autolink plugin runs over our own transaction again, which costs a pass and can
        // only reach the same conclusion.
        editor.view.dispatch(tr.setMeta('preventAutolink', true))
    }
}
